import { Layout } from "@/components/Layout";
import { KeyHolderSection } from "@/pages/services/KeyHolderSection";
import { ServiceCard } from "@/pages/services/ServiceCard";
import { motion } from "framer-motion";
import { Building2, Home } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level animation configs (no per-render allocation)
const FADE_UP = { opacity: 0, y: 20 };
const FADE_IN_PLACE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };
const TRANSITION_DEFAULT = { duration: 0.6 };

const ServicesPage = () => {
  const [b2bServices, setB2bServices] = useState([]);
  const [residentialServices, setResidentialServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const [b2bRes, resRes] = await Promise.all([
          axios.get(`${API}/services/b2b`),
          axios.get(`${API}/services/residential`)
        ]);
        setB2bServices(b2bRes.data);
        setResidentialServices(resRes.data);
      } catch (error) {
        logError("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={FADE_UP}
            animate={FADE_IN_PLACE}
            transition={TRANSITION_DEFAULT}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              Our Services
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1A2E12] tracking-tight mb-6" data-testid="services-title">
              The right clean,<br />
              <span className="text-[#2D5016]">at the right price.</span>
            </h1>
            <p className="text-lg text-[#5C6B57] leading-relaxed">
              Whether it&apos;s an end-of-tenancy turnaround, a recurring weekly clean, or a multi-site commercial contract — we&apos;ve priced it transparently and we&apos;ll quote you in seconds.
            </p>
          </motion.div>
        </div>
      </section>

      {/* B2B Contractual Cleaning */}
      <section className="py-24 bg-white">
        <div className="container-clean">
          <motion.div
            initial={FADE_UP}
            whileInView={FADE_IN_PLACE}
            viewport={VIEWPORT_ONCE}
            className="flex items-center gap-4 mb-12"
          >
            <Building2 className="w-10 h-10 text-[#2D5016]" />
            <div>
              <h2 className="text-3xl font-bold text-[#1A2E12]">Contract cleaning for businesses</h2>
              <p className="text-[#5C6B57]">One supplier, audited standards, predictable monthly invoices.</p>
            </div>
          </motion.div>
          
          <div className="space-y-24">
            {b2bServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} isB2B={true} />
            ))}
          </div>
        </div>
      </section>

      {/* Residential Services */}
      <section className="py-24 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={FADE_UP}
            whileInView={FADE_IN_PLACE}
            viewport={VIEWPORT_ONCE}
            className="flex items-center gap-4 mb-12"
          >
            <Home className="w-10 h-10 text-[#2D5016]" />
            <div>
              <h2 className="text-3xl font-bold text-[#1A2E12]">Cleaning for homes</h2>
              <p className="text-[#5C6B57]">Vetted, fully insured cleaners — trained to a defined Clean Kin standard.</p>
            </div>
          </motion.div>
          
          <div className="space-y-24">
            {residentialServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} isB2B={false} />
            ))}
          </div>
        </div>
      </section>

      {/* Key Holder Management */}
      <KeyHolderSection />

      {/* Pricing Note */}
      <section className="py-16 bg-[#E8F5E9]">
        <div className="container-clean">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-[#1A2E12] mb-4">
              Honest pricing. Quoted upfront.
            </h3>
            <p className="text-[#5C6B57] leading-relaxed">
              You see the full price — including VAT — before you click confirm. No call-out fees, no equipment charges, no &ldquo;final invoice&rdquo; surprises. The number you see is the number you pay.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
